import { z } from 'zod';

export enum CostRange {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  BIWEEKLY = 'BIWEEKLY',
  MONTHLY = 'MONTHLY',
}

export interface Cost {
  id: string;
  amount: number;
  validFrom: Date;
  validTo: Date;
}

export interface CostMode {
  id: string;
  name: string;
  Costs?: Cost[];
}

export interface Schedule {
  id: string;
  from: Date;
  to: Date;
  costModeId: string;
  costMode?: CostMode;
}

export interface DriverApi {
  id: string;
  name: string;
  lastName: string;
  documentNumber: number | null;
  email: string;
  phone: string;
  costRange: CostRange;
  lastCost: string | null;
  umDriverId: string;
  company?: {
    id: string;
    name: string;
  };
  Schedules?: Schedule[];
}

export interface Driver {
  id: string;
  firstName: string;
  lastName: string;
  documentNumber: number | null;
  email: string;
  phone: string;
  costRange: CostRange;
  lastCost: string | null;
  company?: {
    id: string;
    name: string;
  };
  Schedules?: Schedule[];
}

export interface NewDriver {
  name: string;
  lastName: string;
  documentNumber: number;
  email: string;
  phone: string;
  costRange: CostRange;
  companyId: string;
}

export const driverSchema = z.object({
  firstName: z
    .string()
    .min(3, 'Mínimo 3 caracteres')
    .max(25, 'Máximo 25 caracteres'),
  lastName: z
    .string()
    .min(3, 'Mínimo 3 caracteres')
    .max(25, 'Máximo 25 caracteres'),
  documentNumber: z
    .string()
    .min(1, 'El número de documento es requerido')
    .refine((val) => !val.includes('.') && !val.includes('-'), {
      message: 'No debe contener puntos ni guiones',
    })
    .refine((val) => /^\d+$/.test(val), {
      message: 'Debe contener solo números',
    })
    .refine((val) => val.length === 7 || val.length === 8, {
      message: 'Debe tener 7 u 8 dígitos (incluyendo el dígito verificador)',
    }),
  email: z.string().email('Debe ser un correo válido'),
  phone: z
    .string()
    .min(9, 'Debe contener al menos 9 caracteres')
    .max(11, 'Debe contener como máximo 11 caracteres')
    .refine((val) => /^\d+$/.test(val), {
      message: 'El teléfono debe contener solo números',
    }),
  costRange: z.preprocess(
    (val) => (val === '' ? undefined : val),
    z.nativeEnum(CostRange, {
      required_error: 'Selecciona una opción válida',
      invalid_type_error: 'Selecciona una opción válida',
    })
  ),
  companyId: z.string().min(1, 'Debe seleccionar una compañía'),
});

export type DriverSchema = z.infer<typeof driverSchema>;

export const defaultDriverValues: DriverSchema = {
  firstName: '',
  lastName: '',
  documentNumber: '',
  email: '',
  phone: '',
  costRange: CostRange.MONTHLY,
  companyId: '',
};
